// Pure price math shared by the pricing page, checkout and the Telegram bot. A package has its own price
// for its whole length (1 month $5, 3 months $12.50, 1 year $45); Stars are charged at the settings' rate.

export interface PricingSettings {
  starsPerUsd: number;
}

/**
 * The most Stars one invoice asks for: Telegram documents this cap for Star subscriptions and none for one-off
 * invoices, so anything dearer (a year of Silver or Golden) is paid by card or crypto instead.
 */
export const MAX_STARS_PER_INVOICE = 10_000;

/** Telegram Stars charged for a USD amount (whole Stars, at least 1). */
export function starsFor(cents: number, s: PricingSettings): number {
  return Math.max(1, Math.round((cents / 100) * s.starsPerUsd));
}

/** Whether a price can be asked for in one Stars invoice. */
export const starsOk = (cents: number, s: PricingSettings) => starsFor(cents, s) <= MAX_STARS_PER_INVOICE;

export function formatUsd(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
}

export const formatStars = (n: number) => new Intl.NumberFormat("en-US").format(n);

/** "1 month", "3 months", "1 year", "2 years". */
export function monthsName(months: number): string {
  if (months % 12 === 0) return months === 12 ? "1 year" : `${months / 12} years`;
  return months === 1 ? "1 month" : `${months} months`;
}

/** What a month of the package comes to, in cents. */
export const perMonthCents = (cents: number, months: number) => Math.round(cents / Math.max(1, months));

/** How much cheaper a month of the package is than `baseMonthly` cents a month, in whole percent (0 if not). */
export const savingPercent = (cents: number, months: number, baseMonthly: number) =>
  baseMonthly > 0 ? Math.max(0, Math.round(100 - (perMonthCents(cents, months) / baseMonthly) * 100)) : 0;

/** The least a coin may cost in a package or a pack (Admin → Subscriptions, Admin → Game), in cents. */
export const MIN_CENTS_PER_COIN = 1;
